import { useEffect, useRef, useState } from 'react';
import {
  formatDate,
  FORMAT,
  getCurrentWeekDates,
  getDateAfterDays,
} from '../../../../lib/date';
import {
  Battery50Icon,
  CheckCircleIcon as CheckCircleIconSolid,
  CubeTransparentIcon,
} from '@heroicons/react/20/solid';
import { CheckCircleIcon as CheckCircleIconOutline } from '@heroicons/react/24/outline';
import request from '../../../../lib/request';
import ViewDetails from './view';
import Routine from './routine';
import CardioDialog from './complete/cardioDialog';

const UpcomingWeeklyRoutines = () => {
  const dialogRef = useRef(null);
  const routineDialogRef = useRef(null);
  const cardioCompleteDialogRef = useRef(null);

  const [routines, setRoutines] = useState([]);
  const [nextWeek, setNextWeek] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [isRoutineDialogOpen, setIsRoutineDialogOpen] = useState(false);
  const [isCardioDialogOpen, setIsCardioDialogOpen] = useState(false);

  useEffect(() => {
    const nextWeek = getDateAfterDays(new Date(), 7);
    const weeks = getCurrentWeekDates(nextWeek);
    setNextWeek(weeks);
    getWeeklyAssignedRoutine(weeks[0], weeks[weeks.length - 1]);
    getProgramRoutineLog(weeks[0], weeks[weeks.length - 1]);
  }, []);

  const getWeeklyAssignedRoutine = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/program/routines?startDate=${startDate}&endDate=${endDate}`
      );
      if (result) {
        result.forEach((res) => {
          if (res.type === 'ROUTINE' || res.type === 'CHALLENGE') {
            if (!res.routine.cycleRoutine) {
              const setList = [];
              res.routine.sets.reduce((acc, cur, i, arr) => {
                if (i === 0) {
                  acc.setId = cur.setId;
                  acc.setCount = 1;
                  acc.exercises = cur.exercises.map((exe) => ({
                    ...exe.exercise,
                    order: exe.order,
                  }));
                  return acc;
                }

                if (i === arr.length - 1) {
                  if (acc.setId === cur.setId) {
                    acc.setCount++;
                    setList.push(acc);
                  } else {
                    const last = {
                      setId: cur.setId,
                      setCount: 1,
                      exercises: cur.exercises.map((exe) => ({
                        ...exe.exercise,
                        order: exe.order,
                      })),
                    };
                    setList.push(acc, last);
                  }

                  return {};
                }

                if (acc.setId === cur.setId) {
                  acc.setCount++;
                  return acc;
                }

                setList.push(acc);

                return {
                  setId: cur.setId,
                  setCount: 1,
                  exercises: cur.exercises.map((exe) => ({
                    ...exe.exercise,
                    order: exe.order,
                  })),
                };
              }, {});
              res.routine.sets = setList;
            } else {
              res.routine.sets = res.routine.sets.map((s) => ({
                ...s,
                exercises: s.exercises.map((exe) => ({
                  ...exe.exercise,
                  order: exe.order,
                })),
              }));
            }
          }
        });
        setRoutines(result);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getProgramRoutineLog = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/workout/program/routine/log?startDate=${startDate}&endDate=${endDate}`
      );

      if (result) {
        setLogs(result);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openDialog = (routine) => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
      setSelectedRoutine(routine);
    }
  };

  const closeDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const openRoutineDialog = () => {
    if (routineDialogRef.current) {
      routineDialogRef.current.showModal();
      setIsRoutineDialogOpen(true);
    }
  };

  const closeRoutineDialog = () => {
    if (routineDialogRef.current) {
      routineDialogRef.current.close();
      setIsRoutineDialogOpen(false);
    }
  };

  const openCardioDialog = (routine) => {
    if (cardioCompleteDialogRef.current) {
      cardioCompleteDialogRef.current.showModal();
      setSelectedRoutine(routine);
      setIsCardioDialogOpen(true);
    }
  };

  const closeCardioDialog = () => {
    if (cardioCompleteDialogRef.current) {
      cardioCompleteDialogRef.current.close();
      setIsCardioDialogOpen(false);
    }
  };

  return (
    <div>
      <div className='flex items-end gap-2 mb-2'>
        <h2 className='font-bold'>Next Week</h2>
        {nextWeek.length > 0 && (
          <div className='text-white/50 text-xs'>
            ({formatDate(new Date(nextWeek[0]), FORMAT.MONTHDATE)} -{' '}
            {formatDate(
              new Date(nextWeek[nextWeek.length - 1]),
              FORMAT.MONTHDATE
            )}
            )
          </div>
        )}
      </div>
      <div className='flex items-start justify-start gap-4 overflow-x-auto scrollbar-hidden snap-mandatory snap-x'>
        {nextWeek.map((date, i) => (
          <div
            key={i}
            className='flex-shrink-0 w-11/12 snap-always snap-start'
          >
            <div className='text-sm font-bold mb-2'>DAY {i + 1}</div>
            {routines.filter((routine) => routine.date === date).length > 0 ? (
              <div className='space-y-4'>
                {routines
                  .filter((routine) => routine.date === date)
                  .map((routine, j) =>
                    routine.type === 'REST' ? (
                      <div key={j}>
                        <div className='text-sm'>
                          <div className='font-bold mb-2 flex justify-between items-end px-2'>
                            <div>Rest Day</div>
                          </div>
                          <div className='bg-secondary rounded-xl py-10 mb-4 flex justify-center items-center'>
                            <Battery50Icon className='size-6 text-white/50' />
                          </div>
                        </div>
                      </div>
                    ) : routine.type === 'CARDIO' ? (
                      <div key={j}>
                        <div className='text-sm'>
                          <div className='font-bold mb-2 flex justify-between items-end px-2'>
                            <div>{routine.routine.title}</div>
                            {logs.find(
                              (log) => log.logDate === routine.date
                            ) ? (
                              <CheckCircleIconSolid className='size-4 text-accent' />
                            ) : (
                              <CheckCircleIconOutline className='size-4 text-white' />
                            )}
                          </div>
                          <div className='bg-secondary rounded-xl p-4 divide-y-[1px] divide-white/10 mb-4'>
                            {routine.routine.distance ? (
                              <div className='flex gap-2 mb-1 justify-between'>
                                <div>{routine.routine.distance}</div>
                                <div className='text-xs text-white/50 self-end'>
                                  km
                                </div>
                              </div>
                            ) : null}
                            {routine.routine.duration ? (
                              <div className='flex gap-2 mb-1 justify-between'>
                                <div>
                                  {Math.round(routine.routine.duration / 60)}
                                </div>
                                <div className='text-xs text-white/50 self-end'>
                                  min
                                </div>
                              </div>
                            ) : null}
                            {routine.routine.steps ? (
                              <div className='flex gap-2 mb-1 justify-between'>
                                <div>{routine.routine.steps}</div>
                                <div className='text-xs text-white/50 self-end'>
                                  steps
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <button
                          className='rounded-full py-1 w-full border-2 border-accent font-bold'
                          onClick={() => openCardioDialog(routine)}
                        >
                          Record
                        </button>
                      </div>
                    ) : (
                      <div key={j}>
                        <div className='text-sm'>
                          <div className='font-bold mb-2 flex justify-between items-end px-2'>
                            <div>{routine.routine.title}</div>
                            {logs.find(
                              (log) => log.logDate === routine.date
                            ) ? (
                              <CheckCircleIconSolid className='size-4 text-accent' />
                            ) : (
                              <CheckCircleIconOutline className='size-4 text-white' />
                            )}
                          </div>
                          <div className='bg-secondary rounded-xl p-4 divide-y-[1px] divide-white/10 mb-4'>
                            {routine.routine.sets.map((s, k) => (
                              <div
                                key={k}
                                className='flex gap-2 mb-1'
                              >
                                <div className='w-4 text-xs font-bold pt-[2px] text-white/50'>
                                  {k + 1}
                                </div>
                                <div className='flex-1'>
                                  {s.exercises.map((exe, l) => (
                                    <div
                                      key={l}
                                      className='line-clamp-1'
                                    >
                                      {exe.name}
                                    </div>
                                  ))}
                                </div>
                                {!routine.routine.cycleRoutine && (
                                  <div className='text-xs text-white/50 self-end'>
                                    x {s.setCount} sets
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        <button
                          className='rounded-full py-1 w-full border-2 border-accent font-bold'
                          onClick={() => openDialog(routine)}
                        >
                          View details
                        </button>
                      </div>
                    )
                  )}
              </div>
            ) : (
              <div>
                <div className='text-sm'>
                  <div className='font-bold mb-2 px-2 text-white/50'>Empty</div>
                  <div className='bg-secondary rounded-xl p-4 mb-4 flex justify-center items-center'>
                    <CubeTransparentIcon className='size-6 text-white/50' />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <ViewDetails
        dialogRef={dialogRef}
        closeDialog={closeDialog}
        selectedRoutine={selectedRoutine}
        openRoutineDialog={openRoutineDialog}
        isNextWeek={true}
      />
      <Routine
        dialogRef={routineDialogRef}
        closeDialog={closeRoutineDialog}
        selectedRoutine={selectedRoutine}
        open={isRoutineDialogOpen}
        refreshLog={getProgramRoutineLog}
      />
      <CardioDialog
        completeDialogRef={cardioCompleteDialogRef}
        closeDialog={closeCardioDialog}
        isDialogOpen={isCardioDialogOpen}
        routine={selectedRoutine}
        refreshLog={getProgramRoutineLog}
      />
    </div>
  );
};

export default UpcomingWeeklyRoutines;
